import { useWeb3React } from '@web3-react/core';
import { useEffect, useMemo, useState } from 'react';

import useBalance from '@/hooks/useBalance';
import { TokenDenom, TokenId, useTokenPrices } from '@/hooks/useTokenPrices';
import { useAuth } from '@/provider/UserProvider';
import { formatCurrency, getUsdWithToken } from '@/utils';

import {
  LOCK_CONTRACT_ADDRESS,
  LockOption,
  NetworkKey,
  TOKEN_CONTRACT_ADDRESS,
} from '../constants';
import { useLockContext } from '../LockContext';
import { getAllowance, getTokenBalance } from './helpers';

type InfoUser = {
  id: number | string | null | undefined;
  username: string;
  address: string;
  airiBalance: number;
  airiToUsd: number;
};

const MIN_AMOUNT = 1;//hihi sua lai

const useInfoLock = () => {
  const { isLoginClient, auth } = useAuth();
  const context = useWeb3React();
  const { data: prices } = useTokenPrices();
  const { isRefresh, network, isRefreshBalanceBsc, isRefreshBalanceOrai } = useLockContext();
  const account = context.account || '';
  const { airi, getAiriBalance } = useBalance([TokenDenom.AIRI], false);
  const [amount, setAmount] = useState<string>('');
  const [airiBalanceBsc, setAiriBalanceBsc] = useState<number>(0);
  const [option, setOption] = useState<LockOption>(LockOption.ThreeMonths);
  const [validate, setValidate] = useState<string>('');
  const [allowance, setAllowance] = useState<string>('0');
  const airiBalanceOraiNetwork = airi * 10 ** -6;

  const airiBalance = network === NetworkKey.Bsc ? airiBalanceBsc : airiBalanceOraiNetwork;
  const isEnoughBalane =
    network === NetworkKey.Bsc
      ? Number(amount) <= airiBalanceBsc
      : Number(amount) <= airiBalanceOraiNetwork;
  const isShowBalance = network === NetworkKey.Bsc ? !!account : isLoginClient;

  useEffect(() => {
    if (account) {
      getAiriBalanceBsc();
      checkApprove();
    }
  }, [account, isRefresh, isRefreshBalanceBsc]);

  useEffect(() => {
    getAiriBalance();
  }, [isRefreshBalanceOrai, isRefresh, isLoginClient]);

  useEffect(() => {
    setAmount('');
  }, [isRefresh]);

  useEffect(() => {
    checkValidate();
  }, [isEnoughBalane, amount, airiBalance, isRefresh]);

  const checkApprove = async () => {
    const allowance = await getAllowance(
      account,
      TOKEN_CONTRACT_ADDRESS[NetworkKey.Bsc],
      LOCK_CONTRACT_ADDRESS[NetworkKey.Bsc],
    );
    setAllowance(allowance);
  };

  const airiToUsd = useMemo(
    () =>
      getUsdWithToken({
        amount: airiBalance,
        tokenId: TokenId.AIRI,
        prices: prices || {},
      }),
    [prices, airiBalance],
  );

  const getAiriBalanceBsc = async () => {
    const balance = await getTokenBalance(account, TOKEN_CONTRACT_ADDRESS[NetworkKey.Bsc]);
    setAiriBalanceBsc(balance);
  };

  const checkValidate = () => {
    const hasAmount = Number(amount) !== 0 || amount;
    if (!hasAmount) {
      setValidate('Enter amount');
      return;
    }
    if (Number(amount) < MIN_AMOUNT) {
      setValidate(`Min amount is ${formatCurrency(MIN_AMOUNT, 3)}`);
      return;
    }
    if (!isEnoughBalane) {
      setValidate(`Max amount is ${formatCurrency(airiBalance, 3)}`);
      return;
    }
    setValidate('');
  };

  const user: InfoUser = {
    id: auth.user.id,
    username: auth.user.username,
    address: auth.user.publicAddress,
    airiBalance,
    airiToUsd,
  };

  return {
    amount,
    setAmount,
    option,
    setOption,
    isShowBalance,
    isLoginClient,
    user,
    validate,
    accountBsc: account,
    allowance,
  };
};

export default useInfoLock;
